import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { WorkerLayout } from '../../components/layouts/WorkerLayout';
import { MessageCircle, Send, Loader2, Trash2, Bot, User, Camera, X, Image as ImageIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';

const WorkerChat = () => {
  const { api } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/chat/history');
      setMessages(res.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('La imagen no puede superar 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!input.trim() && !selectedImage) || loading) return;

    const userMessage = input.trim();
    const imageToSend = selectedImage;
    
    setInput('');
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    
    // Add user message to UI immediately
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: userMessage || 'Imagen enviada para análisis',
      has_image: !!imageToSend,
      image_preview: imageToSend,
      created_at: new Date().toISOString()
    }]);
    
    setLoading(true);
    try {
      const res = await api.post('/chat/worker', { 
        message: userMessage || 'Analiza esta imagen de la piscina',
        image_base64: imageToSend
      });
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: res.data.response,
        created_at: new Date().toISOString()
      }]);
    } catch (error) {
      toast.error('Error al enviar mensaje');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    try {
      await api.delete('/chat/clear');
      setMessages([]);
      toast.success('Historial eliminado');
    } catch (error) {
      toast.error('Error al eliminar historial');
    }
  };

  return (
    <WorkerLayout>
      <div className="space-y-6 h-[calc(100vh-120px)] flex flex-col">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Asistente Técnico</h1>
            <p className="text-slate-500">Pregunta sobre piscinas o envía fotos para análisis</p>
          </div>
          {messages.length > 0 && (
            <Button variant="outline" onClick={clearHistory} size="sm">
              <Trash2 className="w-4 h-4 mr-2" />
              Limpiar
            </Button>
          )}
        </div>

        <Card className="flex-1 flex flex-col overflow-hidden">
          <CardHeader className="py-3 border-b bg-gradient-to-r from-emerald-500 to-teal-500">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Bot className="w-5 h-5" />
              Experto en Piscinas
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
            <ScrollArea className="flex-1 p-4">
              {loadingHistory ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <MessageCircle className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h3 className="font-semibold text-slate-700 mb-2">¡Hola! Soy tu asistente técnico</h3>
                  <p className="text-slate-500 text-sm max-w-md">
                    Puedo ayudarte con problemas de piscinas. Envíame una foto del problema 
                    o pregúntame sobre tratamiento de agua, productos, y mantenimiento.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2 justify-center">
                    {['¿Cómo trato el agua verde?', '¿Qué hacer si el pH está alto?', '¿Cuánto cloro añadir?'].map((q) => (
                      <button
                        key={q}
                        onClick={() => setInput(q)}
                        className="text-xs px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full hover:bg-emerald-100 transition"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-emerald-600" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                          msg.role === 'user'
                            ? 'bg-emerald-500 text-white rounded-br-md'
                            : 'bg-slate-100 text-slate-800 rounded-bl-md'
                        }`}
                      >
                        {msg.image_preview && (
                          <img 
                            src={msg.image_preview} 
                            alt="Imagen enviada" 
                            className="max-w-xs rounded-lg mb-2"
                          />
                        )}
                        {msg.has_image && !msg.image_preview && (
                          <div className="flex items-center gap-2 mb-1 opacity-75">
                            <ImageIcon className="w-4 h-4" />
                            <span className="text-xs">Imagen adjunta</span>
                          </div>
                        )}
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-slate-600" />
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                        <Bot className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
            
            {/* Image preview */}
            {imagePreview && (
              <div className="px-4 py-2 border-t bg-slate-50">
                <div className="relative inline-block">
                  <img src={imagePreview} alt="Preview" className="h-20 rounded-lg" />
                  <button
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            
            <form onSubmit={sendMessage} className="p-4 border-t bg-white">
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageSelect}
                  ref={fileInputRef}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  className="flex-shrink-0"
                  data-testid="chat-image-btn"
                >
                  <Camera className="w-4 h-4" />
                </Button>
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Escribe o envía una foto..."
                  disabled={loading}
                  className="flex-1"
                  data-testid="chat-input"
                />
                <Button 
                  type="submit" 
                  disabled={loading || (!input.trim() && !selectedImage)} 
                  data-testid="chat-send-btn"
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </WorkerLayout>
  );
};

export default WorkerChat;
